import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Sprout, Sparkles, Droplets, AlertTriangle, CheckCircle2, FlaskConical, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import DisclaimerBanner from "../components/DisclaimerBanner";
import { getPhenologyPhases } from "./Dashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Fertilizer() {
  const { t } = useTranslation();
  const [vineyards, setVineyards] = useState([]);
  const [form, setForm] = useState({
    vineyard_id: "",
    soil_type: "",
    soil_ph: "",
    organic_matter: "",
    last_fertilization: "",
    phenology_phase: "",
    deficiencies_observed: "",
    grape_variety: "",
    objective: "",
    organic_farming: false,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/vineyards`, { withCredentials: true });
        setVineyards(r.data);
      } catch {}
    })();
  }, []);

  const onVineyardChange = (id) => {
    const v = vineyards.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      vineyard_id: id,
      grape_variety: v?.grape_variety || f.grape_variety,
      phenology_phase: v?.phenology_phase || f.phenology_phase,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const hasAny = form.vineyard_id || form.soil_type || form.soil_ph ||
      form.organic_matter || form.last_fertilization || form.phenology_phase ||
      form.deficiencies_observed || form.grape_variety || form.objective || form.notes;
    if (!hasAny) { toast.error(t("fertilizer.errorEmpty")); return; }
    setLoading(true);
    setResult(null);
    try {
      const payload = { ...form };
      if (payload.soil_ph) payload.soil_ph = parseFloat(payload.soil_ph);
      else delete payload.soil_ph;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      const r = await axios.post(`${API}/fertilizer/advice`, payload,
        { withCredentials: true, timeout: 60000 });
      setResult(r.data);
      setTimeout(() => {
        document.getElementById("advice-result")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (e) {
      toast.error(t("fertilizer.errorAI"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="fertilizer-page">
      <Header title={t("fertilizer.headerTitle")} subtitle={t("fertilizer.headerSubtitle")} />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Intro card */}
        <section className="vs-card p-5" style={{ background: "var(--gradient-vine)", color: "white", borderColor: "transparent" }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.3)" }}>
              <Sprout size={22} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <h2 className="heading text-xl font-extrabold leading-tight">{t("fertilizer.introTitle")}</h2>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>
                {t("fertilizer.introSubtitle")}
              </p>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="vs-card p-5" data-testid="fertilizer-form">
          <form onSubmit={submit} className="space-y-3">
            {vineyards.length > 0 && (
              <div>
                <label className="vs-label">{t("fertilizer.vineyardLabel")}</label>
                <select className="vs-input" value={form.vineyard_id}
                  onChange={(e) => onVineyardChange(e.target.value)} data-testid="fert-vineyard">
                  <option value="">{t("fertilizer.vineyardPlaceholder")}</option>
                  {vineyards.map((v) => (
                    <option key={v.id} value={v.id}>{v.name} ({v.location_name})</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="vs-label">{t("fertilizer.soilTypeLabel")}</label>
                <select className="vs-input" value={form.soil_type}
                  onChange={(e) => setForm({ ...form, soil_type: e.target.value })} data-testid="fert-soil">
                  <option value="">—</option>
                  {t("fertilizer.soilTypes", { returnObjects: true }).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="vs-label">{t("fertilizer.phLabel")}</label>
                <input type="number" step="0.1" min="3" max="10" className="vs-input"
                  value={form.soil_ph} onChange={(e) => setForm({ ...form, soil_ph: e.target.value })}
                  placeholder="6.8" data-testid="fert-ph" />
              </div>
            </div>

            <div>
              <label className="vs-label">{t("fertilizer.organicMatterLabel")}</label>
              <select className="vs-input" value={form.organic_matter}
                onChange={(e) => setForm({ ...form, organic_matter: e.target.value })} data-testid="fert-om">
                <option value="">{t("fertilizer.organicMatterPlaceholder")}</option>
                {t("fertilizer.organicMatterLevels", { returnObjects: true }).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label className="vs-label">{t("fertilizer.phenologyLabel")}</label>
              <select className="vs-input" value={form.phenology_phase}
                onChange={(e) => setForm({ ...form, phenology_phase: e.target.value })} data-testid="fert-phase">
                <option value="">—</option>
                {getPhenologyPhases(t).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div>
              <label className="vs-label">{t("fertilizer.grapeVarietyLabel")}</label>
              <input className="vs-input" value={form.grape_variety}
                onChange={(e) => setForm({ ...form, grape_variety: e.target.value })}
                placeholder={t("fertilizer.grapeVarietyPlaceholder")} data-testid="fert-variety" />
            </div>

            <div>
              <label className="vs-label">{t("fertilizer.lastFertilizationLabel")}</label>
              <input className="vs-input" value={form.last_fertilization}
                onChange={(e) => setForm({ ...form, last_fertilization: e.target.value })}
                placeholder={t("fertilizer.lastFertilizationPlaceholder")}
                data-testid="fert-last" />
            </div>

            <div>
              <label className="vs-label">{t("fertilizer.deficienciesLabel")}</label>
              <textarea className="vs-input" rows="2" value={form.deficiencies_observed}
                onChange={(e) => setForm({ ...form, deficiencies_observed: e.target.value })}
                placeholder={t("fertilizer.deficienciesPlaceholder")}
                data-testid="fert-deficiencies" />
            </div>

            <div>
              <label className="vs-label">{t("fertilizer.objectiveLabel")}</label>
              <select className="vs-input" value={form.objective}
                onChange={(e) => setForm({ ...form, objective: e.target.value })} data-testid="fert-objective">
                <option value="">—</option>
                {t("fertilizer.objectives", { returnObjects: true }).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <label className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
              style={{ background: "var(--secondary)", border: "2px solid var(--border)" }}>
              <input type="checkbox" checked={form.organic_farming}
                onChange={(e) => setForm({ ...form, organic_farming: e.target.checked })}
                className="w-5 h-5 accent-green-700" data-testid="fert-organic" />
              <div>
                <div className="font-semibold text-sm">{t("fertilizer.organicFarmingLabel")}</div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {t("fertilizer.organicFarmingHelp")}
                </div>
              </div>
            </label>

            <div>
              <label className="vs-label">{t("fertilizer.notesLabel")}</label>
              <textarea className="vs-input" rows="2" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={t("fertilizer.notesPlaceholder")}
                data-testid="fert-notes" />
            </div>

            <button type="submit" disabled={loading}
              className="vs-btn-primary w-full inline-flex items-center justify-center gap-2"
              style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
              data-testid="fert-submit">
              {loading ? (
                <><div className="vs-spinner" style={{ borderTopColor: "white", borderColor: "rgba(255,255,255,0.3)" }} /> {t("fertilizer.analyzing")}</>
              ) : (
                <><Sparkles size={18} /> {t("fertilizer.submitButton")} <ChevronRight size={18} /></>
              )}
            </button>
          </form>
        </section>

        {/* Result */}
        {result && (
          <section id="advice-result" className="vs-card p-5 vs-fade-up" data-testid="advice-result">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={22} style={{ color: "var(--gold)" }} />
              <h3 className="heading text-xl font-extrabold">{t("fertilizer.resultTitle")}</h3>
            </div>

            {result.summary && (
              <p className="text-base mb-4 p-3 rounded-lg" style={{ background: "var(--secondary)", color: "var(--text-primary)" }}>
                {result.summary}
              </p>
            )}

            {(result.recommendations || []).length > 0 && (
              <div className="space-y-3 mb-4">
                <div className="vs-label">{t("fertilizer.recommendationsLabel")}</div>
                {result.recommendations.map((r, i) => (
                  <div key={i} className="p-4 rounded-xl"
                    style={{ border: "2px solid var(--primary)", background: "var(--surface)" }}
                    data-testid={`reco-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5"
                        style={{ background: "var(--secondary)" }}>
                        <FlaskConical size={18} style={{ color: "var(--primary)" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-base" style={{ color: "var(--text-primary)" }}>
                          {r.product_type}
                        </div>
                        {r.when && (
                          <div className="text-xs mt-0.5" style={{ color: "var(--grape)" }}>
                            📅 <strong>{t("fertilizer.whenLabel")}</strong> {r.when}
                          </div>
                        )}
                        {r.dose && (
                          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                            ⚖️ <strong>{t("fertilizer.doseLabel")}</strong> {r.dose}
                          </div>
                        )}
                        {r.method && (
                          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                            🔧 <strong>{t("fertilizer.howLabel")}</strong> {r.method}
                          </div>
                        )}
                        {r.reason && (
                          <div className="text-sm mt-2 italic" style={{ color: "var(--text-muted)" }}>
                            {r.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(result.soil_management || []).length > 0 && (
              <div className="mb-4">
                <div className="vs-label">{t("fertilizer.soilManagementLabel")}</div>
                <ul className="space-y-1.5">
                  {result.soil_management.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={16} style={{ color: "var(--primary)", marginTop: 2, flexShrink: 0 }} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(result.warnings || []).length > 0 && (
              <div className="mb-4 p-3 rounded-lg" style={{ background: "#FAD3CB", border: "1px solid var(--accent)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={16} style={{ color: "var(--accent)" }} />
                  <span className="font-bold text-sm" style={{ color: "#6B1E0E" }}>{t("fertilizer.warningLabel")}</span>
                </div>
                <ul className="space-y-1 pl-6 list-disc text-sm" style={{ color: "#6B1E0E" }}>
                  {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {result.next_steps && (
              <div className="p-3 rounded-lg flex items-start gap-3"
                style={{ background: "var(--surface)", border: "2px solid var(--gold)" }}>
                <Droplets size={18} style={{ color: "var(--gold)", marginTop: 2 }} />
                <div>
                  <div className="font-bold text-sm mb-0.5">{t("fertilizer.nextStepsLabel")}</div>
                  <div className="text-sm">{result.next_steps}</div>
                </div>
              </div>
            )}

            <p className="text-xs mt-4 pt-3" style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border)" }}>
              <strong>{t("fertilizer.noteLabel")}</strong> {t("fertilizer.disclaimerText")}
            </p>
            <DisclaimerBanner variant="short" />
          </section>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
